import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { FALLBACK_IMAGE, applyFallbackIfNeeded } from '../../utils/imageFallback'

/**
 * VideoTemplatePreview Component
 *
 * Displays a preview of a Remotion video template with template info overlay.
 * Shows the server-rendered preview image with template metadata.
 *
 * Props:
 * - previewUrl: URL to the preview image (SAS URL from backend)
 * - templateId: Template identifier (MinimalistReveal, UrgencyFlashSale, etc.)
 * - variables: Template variables for display
 * - isRendering: Whether video is currently rendering
 * - renderProgress: Optional progress percentage (0-100)
 */

const TEMPLATE_INFO = {
  MinimalistReveal: {
    name: 'Minimalist Reveal',
    description: 'Elegant reveal animation with clean typography',
    icon: '✨',
    color: 'from-slate-500 to-slate-700',
    duration: '15s',
  },
  UrgencyFlashSale: {
    name: 'Flash Sale',
    description: 'High-energy countdown with urgency elements',
    icon: '⚡',
    color: 'from-red-500 to-orange-500',
    duration: '15s',
  },
  SocialProofShowcase: {
    name: 'Social Proof',
    description: 'Trust-building with testimonials and ratings',
    icon: '⭐',
    color: 'from-purple-500 to-indigo-600',
    duration: '15s',
  },
  FeatureDemoFlow: {
    name: 'Feature Demo',
    description: 'Problem-solution narrative with features',
    icon: '🎯',
    color: 'from-teal-500 to-cyan-600',
    duration: '15s',
  },
}

function VideoTemplatePreview({
  previewUrl,
  templateId,
  variables,
  isRendering = false,
  renderProgress,
}) {
  const { t } = useTranslation('approval')

  const templateInfo = TEMPLATE_INFO[templateId] || {
    name: templateId,
    description: 'Video template',
    icon: '🎬',
    color: 'from-gray-500 to-gray-700',
    duration: '15s',
  }

  const displayPrice = variables?.salePrice || variables?.sale_price
  const displayTitle = variables?.productTitle || variables?.product_title

  return (
    <div className="video-template-preview relative">
      {/* Preview Image Container (9:16 aspect ratio) */}
      <div className="aspect-story bg-gray-900 rounded-xl overflow-hidden relative shadow-2xl">
        {/* Preview Image */}
        <img
          src={previewUrl || FALLBACK_IMAGE}
          alt={t('videoPreview.imageAlt', 'Video template preview')}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isRendering ? 'opacity-50' : 'opacity-100'
          }`}
          onError={applyFallbackIfNeeded}
        />

        {/* Template Badge (top-left) */}
        <div className="absolute top-3 left-3">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r ${templateInfo.color} text-white text-xs font-semibold shadow-lg`}>
            <span>{templateInfo.icon}</span>
            <span>{templateInfo.name}</span>
          </div>
        </div>

        {/* Duration Badge (top-right) */}
        <div className="absolute top-3 right-3">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs font-medium">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{templateInfo.duration}</span>
          </div>
        </div>

        {/* Rendering Overlay */}
        {isRendering && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center">
            {/* Animated spinner */}
            <div className="relative w-16 h-16 mb-4">
              <svg className="animate-spin w-16 h-16 text-white" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12" cy="12" r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {/* Progress number in center */}
              {renderProgress !== undefined && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{Math.round(renderProgress)}%</span>
                </div>
              )}
            </div>
            <p className="text-white font-medium mb-1">
              {t('videoPreview.rendering', 'Rendering Video...')}
            </p>
            <p className="text-white/70 text-sm">
              {t('videoPreview.renderingHint', 'This takes 2-3 minutes')}
            </p>
          </div>
        )}

        {/* Product Info Overlay (bottom) */}
        {!isRendering && (displayTitle || displayPrice) && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-12">
            {displayTitle && (
              <p className="text-white font-semibold text-sm line-clamp-2 mb-1">
                {displayTitle}
              </p>
            )}
            {displayPrice && (
              <p className="text-emerald-400 font-bold text-lg">
                {displayPrice}
              </p>
            )}
          </div>
        )}

        {/* Play Button Hint (center, when not rendering) */}
        {!isRendering && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200 bg-black/20">
            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
              <svg className="w-8 h-8 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Template Description (below preview) */}
      <div className="mt-3 px-1">
        <p className="text-xs text-gray-500 text-center">
          {templateInfo.description}
        </p>
      </div>
    </div>
  )
}

VideoTemplatePreview.propTypes = {
  previewUrl: PropTypes.string,
  templateId: PropTypes.string.isRequired,
  variables: PropTypes.object,
  isRendering: PropTypes.bool,
  renderProgress: PropTypes.number,
}

export default VideoTemplatePreview
